import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import authService from '../../services/authService';
import { navigateAfterAuth } from '../../utils/postLoginNavigation';
import { navigateToStaffRoute } from '../../utils/nativeNavigation';
import { trackEvent } from '../../utils/analytics';
import {
  DEFAULT_COUNTRY,
  DEFAULT_PHONE_COUNTRY_CODE,
  PARTNER_COUNTRY_OPTIONS,
  PHONE_COUNTRY_CODES,
  parseStoredPhone,
  phonePlaceholder,
  validatePhoneNumber,
} from '../../constants/phoneCountryCodes';
import { LEGAL } from '../../config/legal';
import { SITE_CONFIG } from '../../config/siteConfig';
import AuthPageLayout from '../AuthPageLayout/AuthPageLayout';
import './PartnerSetup.css';

interface PartnerSetupResponse {
  success: boolean;
  message?: string;
  redirectUrl?: string;
  /** Server-side diagnostic detail (exception type + message); not shown to the user. */
  error?: string;
  phoneOtpRequired?: boolean;
}

interface SlugCheckResponse {
  available: boolean;
  message: string;
}

type PartnerProfile = {
  userName?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  roleId?: number;
};

const PartnerSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.user);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [userName, setUserName] = useState<string>('');
  const [phoneCode, setPhoneCode] = useState<string>(DEFAULT_PHONE_COUNTRY_CODE);
  const [phoneLocal, setPhoneLocal] = useState<string>('');
  const [phoneFromLogin, setPhoneFromLogin] = useState<boolean>(false);
  const [restaurantName, setRestaurantName] = useState<string>('');
  const [slug, setSlug] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [fssaiNumber, setFssaiNumber] = useState<string>('');
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [slugCheckMessage, setSlugCheckMessage] = useState<{ text: string; type: 'available' | 'unavailable' | 'checking' | '' }>({ text: '', type: '' });
  const [slugCheckTimeout, setSlugCheckTimeout] = useState<NodeJS.Timeout | null>(null);
  const [initializing, setInitializing] = useState<boolean>(true);
  const [phoneVerified, setPhoneVerified] = useState<boolean>(false);
  const [otpModalOpen, setOtpModalOpen] = useState<boolean>(false);
  const [otp, setOtp] = useState<string[]>(['', '', '', '']);
  const [otpError, setOtpError] = useState<string>('');
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [otpSending, setOtpSending] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [maskedPhone, setMaskedPhone] = useState<string>('');
  const slugManuallyEdited = useRef(false);
  const otpFirstRef = useRef<HTMLInputElement>(null);
  const pendingRestaurantSubmit = useRef(false);

  const slugifyRestaurantName = (name: string): string => {
    return name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleRestaurantNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRestaurantName(value);
    if (!slugManuallyEdited.current) {
      setSlug(slugifyRestaurantName(value));
      setSlugCheckMessage({ text: '', type: '' });
    }
  };

  const applyUserToForm = (profile: PartnerProfile) => {
    const displayName =
      profile.userName?.trim() ||
      [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim();
    if (displayName) {
      setUserName(displayName);
    }

    const parsed = parseStoredPhone(profile.phoneNumber);
    if (parsed.local) {
      setPhoneCode(parsed.code);
      setPhoneLocal(parsed.local);
      setPhoneFromLogin(true);
    }
    setPhoneVerified(false);

    const hasName = !!displayName;
    const hasPhone = !!(profile.phoneNumber && profile.phoneNumber.trim());
    if (hasName && hasPhone) {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
  };

  useEffect(() => {
    setInitializing(true);
    const finishInit = (profile?: PartnerProfile) => {
      const roleId = profile?.roleId;
      if (roleId === 11) {
        navigate('/supplier/dashboard', { replace: true });
        setInitializing(false);
        return;
      }
      if (roleId !== 8 || !profile) {
        navigate('/signin');
        setInitializing(false);
        return;
      }
      applyUserToForm(profile);
      setInitializing(false);
    };

    const loadPartnerState = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (!response.success || !response.user) {
          navigate('/signin');
          setInitializing(false);
          return;
        }

        const redirectUrl = response.redirectUrl || '/';
        if (redirectUrl === '/supplier/dashboard' || response.user?.roleId === 11) {
          navigate('/supplier/dashboard', { replace: true });
          setInitializing(false);
          return;
        }
        if (redirectUrl !== '/partner/setup') {
          await navigateAfterAuth(redirectUrl, { isPartners: true, navigate, roleId: response.user?.roleId });
          return;
        }

        finishInit(response.user);
      } catch {
        navigate('/signin');
        setInitializing(false);
      }
    };

    if (!user) {
      void loadPartnerState();
      return;
    }

    void (async () => {
      try {
        const response = await authService.getCurrentUser();
        const redirectUrl = response.redirectUrl || '/';
        if (response.success && (redirectUrl === '/supplier/dashboard' || response.user?.roleId === 11)) {
          navigate('/supplier/dashboard', { replace: true });
          return;
        }
        if (response.success && redirectUrl !== '/partner/setup') {
          await navigateAfterAuth(redirectUrl, { isPartners: true, navigate, roleId: response.user?.roleId });
          return;
        }
      } catch {
        // fall through to form init
      }
      finishInit(user);
    })();
  }, [user, navigate]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (otpModalOpen) {
      document.body.style.overflow = 'hidden';
      const t = window.setTimeout(() => otpFirstRef.current?.focus(), 50);
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleCloseOtpModal();
        }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => {
        window.clearTimeout(t);
        document.body.style.overflow = '';
        document.removeEventListener('keydown', onKeyDown);
      };
    }
    document.body.style.overflow = '';
    return undefined;
  }, [otpModalOpen, otpLoading, loading]);

  const checkSlugAvailability = async (slugValue: string) => {
    if (slugValue.length < 3) {
      setSlugCheckMessage({ text: '', type: '' });
      return;
    }

    setSlugCheckMessage({ text: 'Checking availability...', type: 'checking' });

    try {
      const response: SlugCheckResponse = await authService.checkPartnerSlug(slugValue);
      if (response.available) {
        setSlugCheckMessage({ text: response.message, type: 'available' });
      } else {
        setSlugCheckMessage({ text: response.message, type: 'unavailable' });
      }
    } catch {
      setSlugCheckMessage({ text: 'Error checking slug availability', type: 'unavailable' });
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    slugManuallyEdited.current = true;
    let value = e.target.value;
    value = value.toLowerCase();
    value = value.replace(/[^a-z0-9-]/g, '');
    value = value.replace(/-{2,}/g, '-');
    if (value.startsWith('-')) {
      value = value.substring(1);
    }
    setSlug(value);

    if (slugCheckTimeout) {
      clearTimeout(slugCheckTimeout);
      setSlugCheckTimeout(null);
    }

    if (value.length < 3) {
      setSlugCheckMessage({ text: '', type: '' });
    }
  };

  const handleSlugBlur = () => {
    let cleanedSlug = slug;
    if (cleanedSlug.endsWith('-')) {
      cleanedSlug = cleanedSlug.slice(0, -1);
      setSlug(cleanedSlug);
    }
    if (cleanedSlug.length >= 3) {
      checkSlugAvailability(cleanedSlug);
    }
  };

  const handlePhoneLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    const maxLen = phoneCode === '+91' ? 10 : 15;
    setPhoneLocal(digits.slice(0, maxLen));
  };

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userName.trim()) {
      setError('Name is required');
      return;
    }

    if (!phoneFromLogin) {
      const phoneErr = validatePhoneNumber(phoneLocal, phoneCode);
      if (phoneErr) {
        setError(phoneErr);
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response: PartnerSetupResponse = await authService.savePartnerName(
        userName.trim(),
        phoneFromLogin ? undefined : phoneCode,
        phoneFromLogin ? undefined : phoneLocal
      );
      if (response.success) {
        setPhoneVerified(false);
        setSuccess(response.message || 'Details saved successfully');
        setTimeout(() => {
          setCurrentStep(2);
          setSuccess('');
        }, 1000);
      } else {
        setError(response.message || 'Failed to save details. Please try again.');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setError(axiosErr.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const displayPhone = maskedPhone || `${phoneCode} ${phoneLocal}`.trim();

  const sendPhoneOtp = async (openModal: boolean): Promise<boolean> => {
    setOtpSending(true);
    setOtpError('');
    try {
      const response = await authService.sendPartnerPhoneOtp();
      if (response.maskedPhone) {
        setMaskedPhone(response.maskedPhone);
      }
      const cooldownSeconds = response.otpCooldownSeconds || 30;
      if (response.success) {
        setResendCooldown(cooldownSeconds);
        if (openModal) {
          setOtp(['', '', '', '']);
          setOtpModalOpen(true);
        }
        return true;
      }
      setOtpError(response.message || 'Could not send OTP. Please try again.');
      if (openModal && response.otpStillValid) {
        setOtpModalOpen(true);
        return true;
      }
      setError(response.message || 'Could not send OTP. Please try again.');
      return false;
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; otpStillValid?: boolean; maskedPhone?: string } } };
      const data = axiosErr.response?.data;
      if (data?.maskedPhone) {
        setMaskedPhone(data.maskedPhone);
      }
      const message = data?.message || 'Could not send OTP. Please try again.';
      setOtpError(message);
      if (openModal && data?.otpStillValid) {
        setOtpModalOpen(true);
        return true;
      }
      setError(message);
      return false;
    } finally {
      setOtpSending(false);
    }
  };

  const createRestaurant = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response: PartnerSetupResponse = await authService.savePartnerRestaurant(
        restaurantName.trim(),
        slug,
        city.trim(),
        country.trim(),
        acceptTerms,
        fssaiNumber.trim() || undefined
      );
      if (response.success) {
        trackEvent('restaurant_created', { app_surface: 'restaurant_staff' });
        pendingRestaurantSubmit.current = false;
        setOtpModalOpen(false);
        setSuccess(response.message || 'Restaurant created successfully!');
        setTimeout(() => {
          void navigateToStaffRoute(response.redirectUrl || '/dashboard');
        }, 1500);
      } else if (response.phoneOtpRequired) {
        setPhoneVerified(false);
        pendingRestaurantSubmit.current = true;
        await sendPhoneOtp(true);
      } else {
        if (response.error) {
          console.error('Partner setup failed:', response.error);
        }
        setError(response.message || 'Failed to create restaurant. Please try again.');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; phoneOtpRequired?: boolean } } };
      if (axiosErr.response?.data?.phoneOtpRequired) {
        setPhoneVerified(false);
        pendingRestaurantSubmit.current = true;
        await sendPhoneOtp(true);
      } else {
        setError(
          axiosErr.response?.data?.message ||
            (err as Error).message ||
            'An error occurred. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRestaurant = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!restaurantName.trim()) {
      setError('Restaurant name is required');
      return;
    }

    if (!slug.trim()) {
      setError('Slug is required');
      return;
    }

    if (!slug.match(/^[a-z0-9-]+$/)) {
      setError('Slug can only contain lowercase letters, numbers, and hyphens');
      return;
    }

    if (!city.trim()) {
      setError('City is required');
      return;
    }

    if (!country.trim()) {
      setError('Country is required');
      return;
    }

    if (fssaiNumber.trim() && !/^[0-9]{14}$/.test(fssaiNumber.trim())) {
      setError('Enter a valid 14-digit FSSAI registration or licence number, or leave it blank.');
      return;
    }

    if (!acceptTerms) {
      setError('You must accept the Terms & Conditions and Privacy Policy to continue.');
      return;
    }

    if (slugCheckMessage.type === '' || slugCheckMessage.type === 'checking') {
      setSlugCheckMessage({ text: 'Checking availability...', type: 'checking' });
      try {
        const checkResponse = await authService.checkPartnerSlug(slug);
        if (!checkResponse.available) {
          setError(checkResponse.message || 'This slug is not available. Please choose another one.');
          setSlugCheckMessage({ text: checkResponse.message, type: 'unavailable' });
          return;
        }
        setSlugCheckMessage({ text: checkResponse.message, type: 'available' });
      } catch {
        setError('Error checking slug availability. Please try again.');
        return;
      }
    } else if (slugCheckMessage.type === 'unavailable') {
      setError('Please choose a different slug. The current one is not available.');
      return;
    }

    setError('');
    setSuccess('');

    if (!phoneVerified) {
      pendingRestaurantSubmit.current = true;
      setLoading(true);
      const sent = await sendPhoneOtp(true);
      setLoading(false);
      if (!sent) {
        pendingRestaurantSubmit.current = false;
      }
      return;
    }

    await createRestaurant();
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) {
      document.getElementById(`partner-otp-digit-${index + 2}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`partner-otp-digit-${index}`)?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').trim().replace(/[^0-9]/g, '').slice(0, 4).split('');
    if (digits.length === 0) {
      return;
    }
    const newOtp = [...otp];
    digits.forEach((digit, idx) => {
      if (idx < 4) {
        newOtp[idx] = digit;
      }
    });
    setOtp(newOtp);
    const lastFilledIndex = Math.min(digits.length - 1, 3);
    document.getElementById(`partner-otp-digit-${lastFilledIndex + 1}`)?.focus();
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpValue = otp.join('');
    if (otpValue.length !== 4) {
      setOtpError('Enter the 4-digit OTP sent to your phone.');
      return;
    }

    setOtpLoading(true);
    setOtpError('');
    try {
      const response = await authService.verifyPartnerPhoneOtp(otpValue);
      if (response.success) {
        setPhoneVerified(true);
        setOtpModalOpen(false);
        if (pendingRestaurantSubmit.current) {
          await createRestaurant();
        }
      } else {
        setOtpError(response.message || 'Invalid or expired OTP');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setOtpError(axiosErr.response?.data?.message || 'Invalid or expired OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleCloseOtpModal = () => {
    if (otpLoading || loading) {
      return;
    }
    pendingRestaurantSubmit.current = false;
    setOtpModalOpen(false);
    setOtpError('');
  };

  const handleBack = () => {
    setError('');
    setSuccess('');
    setCurrentStep(1);
  };

  if (initializing) {
    return (
      <AuthPageLayout>
        <div className="partner-setup-container">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '18px', color: 'var(--text-secondary, #666)' }}>Loading...</div>
          </div>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <div className="partner-setup-container">
        <div className="partner-setup-wrapper">
          <div className="partner-setup-form">
            <h2 className="partner-setup-title">Complete Your Partner Setup</h2>
            <p className="partner-setup-subtitle">Follow these steps to get started</p>

            <div className="stepper">
              <div className={`step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`} id="step1">
                <div className="step-circle" aria-hidden="true">{currentStep > 1 ? '' : '1'}</div>
                <div className="step-label">Your Details</div>
              </div>
              <div className={`step ${currentStep >= 2 ? 'active' : ''}`} id="step2">
                <div className="step-circle">2</div>
                <div className="step-label">Restaurant Details</div>
              </div>
            </div>

            {error && <div className="error-message" role="alert">{error}</div>}
            {success && <div className="success-message" role="alert">{success}</div>}

            {currentStep === 1 && (
              <div className="step-content active">
                <h3>Your Details</h3>
                <form onSubmit={handleSaveName}>
                  <div className="input-group-wrapper">
                    <label htmlFor="userName">Full Name *</label>
                    <input
                      type="text"
                      id="userName"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      className="form-control"
                      placeholder="Enter your full name"
                      autoComplete="name"
                      required
                    />
                  </div>

                  <div className="input-group-wrapper">
                    <label htmlFor="partnerPhone">Mobile Number *</label>
                    <div className="phone-input-row">
                      <select
                        id="partnerPhoneCode"
                        className="form-control phone-code-select"
                        value={phoneCode}
                        onChange={(e) => setPhoneCode(e.target.value)}
                        disabled={phoneFromLogin}
                        aria-label="Country code"
                      >
                        {PHONE_COUNTRY_CODES.map((c) => (
                          <option key={`${c.country}-${c.code}`} value={c.code}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        id="partnerPhone"
                        value={phoneLocal}
                        onChange={handlePhoneLocalChange}
                        className="form-control"
                        placeholder={phonePlaceholder(phoneCode)}
                        autoComplete="tel-national"
                        inputMode="numeric"
                        disabled={phoneFromLogin}
                        required={!phoneFromLogin}
                      />
                    </div>
                    {phoneFromLogin ? (
                      <small>Phone number from your login account.</small>
                    ) : (
                      <small>We&apos;ll send order and account alerts to this number.</small>
                    )}
                  </div>

                  <div className="step-actions">
                    <button type="submit" className="btn-primary" disabled={loading}>
                      {loading ? 'Saving...' : 'Continue'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {currentStep === 2 && (
              <div className="step-content active">
                <h3>Restaurant Information</h3>
                <p className="partner-otp-hint">
                  We&apos;ll verify {phoneLocal ? `${phoneCode} ${phoneLocal}` : 'your mobile number'} with a WhatsApp OTP before creating the restaurant.
                </p>
                <form onSubmit={handleSaveRestaurant}>
                  <div className="input-group-wrapper">
                    <label htmlFor="restaurantName">Restaurant Name *</label>
                    <input
                      type="text"
                      id="restaurantName"
                      value={restaurantName}
                      onChange={handleRestaurantNameChange}
                      className="form-control"
                      placeholder="Enter your restaurant name"
                      autoComplete="organization"
                      required
                    />
                  </div>

                  <div className="input-group-wrapper">
                    <label htmlFor="slug">Restaurant Slug *</label>
                    <input
                      type="text"
                      id="slug"
                      value={slug}
                      onChange={handleSlugChange}
                      onBlur={handleSlugBlur}
                      className="form-control"
                      placeholder="e.g., my-restaurant"
                      pattern="[a-z0-9-]+"
                      autoComplete="off"
                      required
                    />
                    {slugCheckMessage.text && (
                      <div className={`slug-check-message ${slugCheckMessage.type}`}>
                        {slugCheckMessage.text}
                      </div>
                    )}
                    <small>
                      Auto-filled from restaurant name; edit if needed. Lowercase letters, numbers, and hyphens only.
                    </small>
                  </div>

                  <div className="form-row-two">
                    <div className="input-group-wrapper">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="form-control"
                        placeholder="e.g., Bangalore"
                        autoComplete="address-level2"
                        required
                      />
                    </div>
                    <div className="input-group-wrapper">
                      <label htmlFor="country">Country *</label>
                      <select
                        id="country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="form-control"
                        autoComplete="country-name"
                        required
                      >
                        {PARTNER_COUNTRY_OPTIONS.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="input-group-wrapper">
                    <label htmlFor="fssaiNumber">FSSAI registration / licence number (optional)</label>
                    <input
                      type="text"
                      id="fssaiNumber"
                      inputMode="numeric"
                      pattern="[0-9]{14}"
                      maxLength={14}
                      value={fssaiNumber}
                      onChange={(e) => setFssaiNumber(e.target.value.replace(/\D/g, '').slice(0, 14))}
                      className="form-control"
                      placeholder="14-digit FSSAI number"
                      autoComplete="off"
                    />
                    <small>
                      Optional. 14 digits only. Peppar will record it for verification; you can add it later if you do not have it now.
                    </small>
                  </div>

                  <div className="partner-consent">
                    <input
                      id="acceptTerms"
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      required
                    />
                    <label htmlFor="acceptTerms">
                      I am authorised to bind this restaurant. I have read and agree to the{' '}
                      <a
                        href={`${SITE_CONFIG.urls.site}/terms-and-conditions`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms &amp; Conditions
                      </a>{' '}
                      and{' '}
                      <a
                        href={`${SITE_CONFIG.urls.site}/privacy-policy`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>{' '}
                      of Peppar Enterprises (version {LEGAL.documentVersion}).
                    </label>
                  </div>

                  <div className="step-actions">
                    <button type="button" className="btn-secondary" onClick={handleBack} disabled={loading}>
                      Back
                    </button>
                    <button type="submit" className="btn-primary" disabled={loading || otpSending || slugCheckMessage.type === 'unavailable' || !acceptTerms}>
                      {loading || otpSending ? (phoneVerified ? 'Creating...' : 'Sending OTP...') : 'Complete Setup'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
      {otpModalOpen && createPortal(
        <div
          className="partner-otp-overlay"
          onClick={handleCloseOtpModal}
          role="presentation"
        >
          <div
            className="partner-otp-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="partner-otp-title"
          >
            <button
              type="button"
              className="partner-otp-close"
              onClick={handleCloseOtpModal}
              aria-label="Close"
              disabled={otpLoading || loading}
            >
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
            <h3 id="partner-otp-title">Verify mobile number</h3>
            <p className="partner-otp-copy">
              Enter the 4-digit code we sent on WhatsApp to {displayPhone || 'your phone'}.
            </p>
            <form onSubmit={handleVerifyOtp}>
              {otpError && <div className="error-message" role="alert">{otpError}</div>}
              <div className="partner-otp-digits" role="group" aria-label="One-time password">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={index === 0 ? otpFirstRef : undefined}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    id={`partner-otp-digit-${index + 1}`}
                    className="partner-otp-input"
                    maxLength={1}
                    value={digit}
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    onChange={(e) => handleOtpChange(index, e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    disabled={otpLoading || loading}
                  />
                ))}
              </div>
              <p className="partner-otp-resend">
                Didn&apos;t get an OTP?{' '}
                {resendCooldown > 0 ? (
                  <span className="partner-otp-resend-wait">Resend OTP in {resendCooldown}s</span>
                ) : (
                  <button
                    type="button"
                    className="partner-otp-resend-btn"
                    onClick={() => {
                      if (!otpSending && !otpLoading) {
                        void sendPhoneOtp(false);
                      }
                    }}
                    disabled={otpSending || otpLoading}
                  >
                    {otpSending ? 'Sending…' : 'Resend OTP'}
                  </button>
                )}
              </p>
              <button type="submit" className="btn-primary partner-otp-submit" disabled={otpLoading || loading || otp.join('').length !== 4}>
                {otpLoading || loading ? 'Verifying...' : 'Verify and continue'}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}
    </AuthPageLayout>
  );
};

export default PartnerSetup;
